import { httpClient } from '@/shared/api';
import type { SchoolWithFiltersItemDto, SchoolWithFiltersPagedResult } from './schoolWithFilters.dto';

const PREFIX = '/app/school';

function mapItem(raw: unknown): SchoolWithFiltersItemDto {
  if (!raw || typeof raw !== 'object') {
    return { id: '' };
  }
  const o = raw as Record<string, unknown>;
  const ts = o.totalStudentCount ?? o.TotalStudentCount;
  const tuitionRaw = o.tuitionUSD ?? o.TuitionUSD;
  const tuitionUSD =
    typeof tuitionRaw === 'number' && Number.isFinite(tuitionRaw)
      ? tuitionRaw
      : Number(tuitionRaw) || undefined;
  return {
    id: String(o.id ?? o.Id ?? ''),
    schoolName: (o.schoolName ?? o.SchoolName) as string | null | undefined,
    educationalCenterNumber: (o.educationalCenterNumber ?? o.EducationalCenterNumber) as
      | string
      | null
      | undefined,
    code: (o.code ?? o.Code) as string | null | undefined,
    totalStudentCount:
      typeof ts === 'number' && Number.isFinite(ts) ? ts : Number(ts) || 0,
    governorateName: (o.governorateName ?? o.GovernorateName) as string | null | undefined,
    cityName: (o.cityName ?? o.CityName) as string | null | undefined,
    areaName: (o.areaName ?? o.AreaName) as string | null | undefined,
    tuitionUSD,
  };
}

function normalizePaged(raw: unknown): SchoolWithFiltersPagedResult {
  if (!raw || typeof raw !== 'object') {
    return { items: [], totalCount: 0 };
  }
  const o = raw as Record<string, unknown>;
  const rawItems = (o.items ?? o.Items) as unknown[] | undefined;
  const items = Array.isArray(rawItems) ? rawItems.map(mapItem) : [];
  const tc = o.totalCount ?? o.TotalCount;
  const totalCount = typeof tc === 'number' && Number.isFinite(tc) ? tc : items.length;
  return { items, totalCount };
}

function toQuery(params: {
  CityId?: number;
  AreaId?: number;
  GovernorateId?: number;
  Year?: number;
  Keyword?: string;
  SchoolIds?: string[];
  Sorting?: string;
  SkipCount?: number;
  MaxResultCount?: number;
}): string {
  const search = new URLSearchParams();
  if (params.CityId != null) search.set('CityId', String(params.CityId));
  if (params.AreaId != null) search.set('AreaId', String(params.AreaId));
  if (params.GovernorateId != null) search.set('GovernorateId', String(params.GovernorateId));
  if (params.Year != null && Number.isFinite(params.Year)) search.set('Year', String(params.Year));
  if (params.Keyword != null && params.Keyword !== '') search.set('Keyword', params.Keyword);
  if (params.Sorting != null && params.Sorting !== '') search.set('Sorting', params.Sorting);
  if (params.SkipCount != null) search.set('SkipCount', String(params.SkipCount));
  if (params.MaxResultCount != null) search.set('MaxResultCount', String(params.MaxResultCount));
  if (params.SchoolIds?.length) {
    for (const id of params.SchoolIds) {
      if (id?.trim()) search.append('SchoolIds', id.trim());
    }
  }
  const q = search.toString();
  return q ? `?${q}` : '';
}

export type SchoolWithFiltersListParams = {
  schoolIds?: string[];
  /** When `true`, list schools outside parent-market bands (parent tuition context). Omit when unused. */
  getOutsideIds?: boolean;
  keyword?: string;
  cityId?: number;
  areaId?: number;
  governorateId?: number;
  /** Reporting / budget year when the API filters by year. */
  year?: number;
  skipCount?: number;
  maxResultCount?: number;
  sorting?: string;
  /** Used with `sorting` when the server supports it (e.g. `TotalStudentCount`). */
  dir?: 'asc' | 'desc';
};

function buildWithFiltersBody(params: SchoolWithFiltersListParams): Record<string, unknown> {
  const ids = (params.schoolIds ?? []).map((id) => id.trim()).filter(Boolean);
  const body: Record<string, unknown> = {
    schoolIds: ids,
    skipCount: params.skipCount ?? 0,
    maxResultCount: params.maxResultCount ?? 100,
  };
  if (params.keyword != null && params.keyword !== '') body.keyword = params.keyword;
  if (params.cityId != null) body.cityId = params.cityId;
  if (params.areaId != null) body.areaId = params.areaId;
  if (params.governorateId != null) body.governorateId = params.governorateId;
  if (params.year != null && Number.isFinite(params.year)) body.year = params.year;
  if (params.getOutsideIds !== undefined) body.getOutsideIds = params.getOutsideIds;
  if (params.sorting != null && params.sorting !== '') body.sorting = params.sorting;
  if (params.dir != null) body.dir = params.dir;
  return body;
}

export const schoolWithFiltersApi = {
  /**
   * GET /api/app/school/with-filters — paged schools (e.g. filter by SchoolIds).
   * Prefer {@link postList} when `schoolIds` may exceed URL length limits.
   */
  getList: async (params: SchoolWithFiltersListParams): Promise<SchoolWithFiltersPagedResult> => {
    const raw = await httpClient.get<unknown>(
      `${PREFIX}/with-filters${toQuery({
        CityId: params.cityId,
        AreaId: params.areaId,
        GovernorateId: params.governorateId,
        Year: params.year,
        Keyword: params.keyword,
        SchoolIds: params.schoolIds,
        SkipCount: params.skipCount ?? 0,
        MaxResultCount: params.maxResultCount ?? 100,
        Sorting: params.sorting,
      })}`
    );
    return normalizePaged(raw);
  },

  /**
   * POST /api/app/school/list-with-filters — paged schools; body carries `schoolIds` (large lists).
   * Does not send `sorting` (server default order).
   */
  postList: async (params: SchoolWithFiltersListParams): Promise<SchoolWithFiltersPagedResult> => {
    const raw = await httpClient.post<unknown>(
      `${PREFIX}/list-with-filters`,
      buildWithFiltersBody(params)
    );
    return normalizePaged(raw);
  },
};
